/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  Activity,
  BarChart3,
  ChevronRight,
  Command,
  Cpu,
  Facebook,
  Instagram,
  Layers,
  Linkedin,
  Menu,
  Rss,
  Search,
  Send,
  ShieldCheck,
  TrendingUp,
  Twitter,
  X,
  Zap,
  ZapIcon,
} from "lucide-react";
import {
  AnimatePresence,
  motion,
  useScroll,
  useSpring,
  useTransform,
} from "motion/react";
import React, { useEffect, useRef, useState } from "react";
import { PRIVACY_POLICY, TERMS_AND_CONDITIONS } from "./constants/legal";

// --- Components ---

const Preloader = ({ onComplete }: { onComplete: () => void }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(onComplete, 800);
          return 100;
        }
        return prev + Math.floor(Math.random() * 10) + 1;
      });
    }, 60);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <motion.div
      exit={{
        clipPath: "inset(0 0 100% 0)",
        transition: { duration: 1.2, ease: [0.76, 0, 0.24, 1] },
      }}
      className="fixed inset-0 z-[200] bg-ink flex flex-col items-center justify-center text-white overflow-hidden px-4"
    >
      <div className="absolute inset-0 noise opacity-10 pointer-events-none" />

      <div className="relative overflow-hidden mb-4 sm:mb-8 w-full max-w-sm flex justify-center">
        <motion.div
          initial={{ y: 80 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-center min-w-0"
        >
          <img
            src="/Logo Horizontal Color White.svg"
            alt="Blockchain Wire"
            className="h-10 sm:h-14 w-auto max-w-full object-contain"
          />
        </motion.div>
      </div>

      <div className="w-full max-w-[18rem] sm:w-80 h-0.5 sm:h-[2px] bg-white/5 relative overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${count}%` }}
          className="absolute inset-0 bg-primary shadow-[0_0_20px_rgba(53,186,207,0.5)]"
        />
      </div>

      <div className="mt-4 sm:mt-8 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.4em] sm:tracking-[0.8em] text-white/20 flex items-center gap-2 sm:gap-4 flex-wrap justify-center">
        <span className="animate-pulse">Initializing_Protocol</span>
        <span className="text-primary font-bold">
          {count.toString().padStart(3, "0")}%
        </span>
      </div>

      <div className="absolute bottom-6 sm:bottom-12 left-1/2 -translate-x-1/2 text-[7px] sm:text-[8px] font-mono text-white/10 uppercase tracking-[0.2em] sm:tracking-[0.5em] text-center max-w-[90vw] px-2">
        Content_Syndicate_Authority_Engine_v4.0.2
      </div>
    </motion.div>
  );
};

const SIGNUP_URL = "https://admin.blockchainwire.io/signup/client";
const CONTACT_URL = "https://blockchainwire.io/contact";

const AgencyButton = ({
  children,
  className = "",
  onClick,
  href,
  external = false,
  variant = "primary",
  size = "md",
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  href?: string;
  external?: boolean;
  variant?: "primary" | "outline" | "white" | "ghost";
  size?: "sm" | "md" | "lg";
}) => {
  const elRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const [btnPos, setBtnPos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!elRef.current) return;
    const { left, top, width, height } = elRef.current.getBoundingClientRect();
    const x = (e.clientX - (left + width / 2)) * 0.3;
    const y = (e.clientY - (top + height / 2)) * 0.3;
    setBtnPos({ x, y });
  };

  const handleMouseLeave = () => {
    setBtnPos({ x: 0, y: 0 });
  };

  const variants = {
    primary:
      "bg-primary text-white hover:bg-primary-dark shadow-lg shadow-primary/20",
    outline:
      "bg-transparent border border-ink/20 text-ink hover:border-primary hover:text-primary",
    white: "bg-white text-ink hover:bg-slate-100 shadow-xl shadow-black/5",
    ghost: "bg-transparent text-ink hover:bg-ink/5",
  };

  const sizes = {
    sm: "py-1.5 px-4 text-[10px] sm:py-2 sm:px-6 sm:text-xs",
    md: "py-2.5 px-5 text-xs sm:py-5 sm:px-10 sm:text-sm",
    lg: "py-3 px-6 text-xs sm:py-8 sm:px-16 sm:text-lg",
  };

  const baseClass = `relative overflow-hidden font-bold rounded-full transition-all duration-500 flex items-center justify-center gap-2 cursor-pointer group ${variants[variant]} ${sizes[size]} ${className}`;

  if (href) {
    return (
      <motion.a
        ref={elRef as React.RefObject<HTMLAnchorElement>}
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        animate={{ x: btnPos.x, y: btnPos.y }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={baseClass}
      >
        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[0.76, 0, 0.24, 1]" />
        <span className="relative z-10 flex items-center gap-2 tracking-widest uppercase whitespace-nowrap">
          {children}
        </span>
      </motion.a>
    );
  }

  return (
    <motion.button
      ref={elRef as React.RefObject<HTMLButtonElement>}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      animate={{ x: btnPos.x, y: btnPos.y }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={baseClass}
    >
      <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[0.76, 0, 0.24, 1]" />
      <span className="relative z-10 flex items-center gap-2 tracking-widest uppercase whitespace-nowrap">
        {children}
      </span>
    </motion.button>
  );
};

const TextReveal = ({
  children,
  className = "",
  delay = 0,
}: {
  children: string;
  className?: string;
  delay?: number;
}) => {
  return (
    <div className={`overflow-hidden py-2 px-4 -mx-4 ${className}`}>
      <motion.div
        initial={{ y: "105%" }}
        whileInView={{ y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
};

const Marquee = ({
  children,
  reverse = false,
}: {
  children: React.ReactNode;
  reverse?: boolean;
}) => (
  <div className="relative flex overflow-x-hidden border-y border-ink/5 py-8 sm:py-12 lg:py-16 bg-white/50 backdrop-blur-sm min-h-[4rem] sm:min-h-[5.5rem]">
    <div
      className={`${reverse ? "animate-marquee-reverse" : "animate-marquee"} whitespace-nowrap flex items-center gap-12 sm:gap-20 lg:gap-24 px-6 sm:px-12`}
    >
      {children}
      {children}
    </div>
  </div>
);

// --- Main App ---

const LegalModal = ({
  isOpen,
  onClose,
  title,
  content,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-ink/80 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 40 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl max-h-[85vh] bg-paper rounded-[3rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] flex flex-col border border-ink/5"
          >
            <div className="p-10 lg:p-16 border-b border-ink/5 flex justify-between items-center bg-white/50 backdrop-blur-md">
              <h2 className="text-3xl lg:text-5xl font-display font-black uppercase tracking-tighter leading-none">
                {title}
              </h2>
              <button
                onClick={onClose}
                className="w-14 h-14 flex items-center justify-center hover:bg-ink hover:text-white rounded-full transition-all duration-500 group"
              >
                <X className="w-8 h-8 group-hover:rotate-90 transition-transform" />
              </button>
            </div>
            <div className="p-10 lg:p-16 overflow-y-auto font-serif text-xl lg:text-2xl leading-relaxed text-ink/70 whitespace-pre-wrap selection:bg-primary/20">
              {content}
            </div>
            <div className="p-10 lg:p-16 border-t border-ink/5 flex justify-end bg-white/50 backdrop-blur-md">
              <AgencyButton variant="primary" size="lg" onClick={onClose}>
                Close Document
              </AgencyButton>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [legalModal, setLegalModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
  }>({
    isOpen: false,
    title: "",
    content: "",
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const heroY = useTransform(smoothProgress, [0, 0.2], [0, -100]);
  const heroScale = useTransform(smoothProgress, [0, 0.2], [1, 0.9]);
  const heroOpacity = useTransform(smoothProgress, [0, 0.15], [1, 0]);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const pricingPlans = [
    {
      name: "Pro",
      includes: [
        "Benzinga",
        "Street Insider",
        "Google Indexing",
        "300+ Placements",
      ],
      price: "Growth",
      highlight: false,
    },
    {
      name: "Elite",
      includes: [
        "Business Insider",
        "Barchart",
        "Street Insider",
        "500+ Placements",
      ],
      price: "Most Popular",
      highlight: true,
    },
    {
      name: "Premium",
      includes: ["Pro Circuit", "AP News", "MarketWatch", "500+ Placements"],
      price: "Ultimate",
      highlight: false,
    },
    {
      name: "Copper",
      includes: ["35 + crypto new sites", "Google Indexing", "300+ Placements"],
      price: "Essential",
      highlight: false,
    },
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen">
      <AnimatePresence>
        {isLoading && <Preloader onComplete={() => setIsLoading(false)} />}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: isLoading ? 0 : 1, y: isLoading ? 20 : 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      >
        {/* Noise Overlay */}
        <div className="fixed inset-0 pointer-events-none noise z-[150]" />

        {/* Scroll Progress Indicator */}
        <div className="fixed top-0 left-0 right-0 h-1 z-[200] pointer-events-none">
          <motion.div
            style={{ scaleX: smoothProgress }}
            className="h-full bg-primary origin-left shadow-[0_0_20px_rgba(53,186,207,0.5)]"
          />
        </div>

        {/* Floating Navigation */}
        <nav className="fixed top-4 left-0 right-0 z-[100] px-4 sm:top-8 sm:px-6">
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-5xl mx-auto"
          >
            <div className="bg-white/90 backdrop-blur-xl rounded-full px-4 py-3 sm:px-6 sm:py-5 lg:px-10 flex justify-between items-center shadow-premium border border-white/20 min-w-0">
              <a
                href="/"
                className="flex items-center gap-2 sm:gap-3 group min-w-0"
              >
                <img
                  src="/Logo Horizontal Color.svg"
                  alt="Blockchain Wire"
                  className="h-6 sm:h-8 w-auto object-contain"
                />
              </a>

              <div className="hidden md:flex items-center gap-10">
                {["Authority", "Strategy", "Launchpads", "Process"].map(
                  (item) => (
                    <a
                      key={item}
                      href={`#${item.toLowerCase()}`}
                      className="text-[9px] font-bold uppercase tracking-[0.3em] text-ink/60 hover:text-primary transition-colors relative group"
                    >
                      {item}
                      <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary transition-all duration-300 group-hover:w-full" />
                    </a>
                  ),
                )}
              </div>

              <div className="flex items-center gap-4">
                <AgencyButton
                  variant="primary"
                  size="sm"
                  className="hidden sm:flex"
                  href={SIGNUP_URL}
                  external
                >
                  Claim Discount
                </AgencyButton>
                <button
                  className="md:hidden p-2 text-ink"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  {isMenuOpen ? <X /> : <Menu />}
                </button>
              </div>
            </div>
          </motion.div>
        </nav>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: "-100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "-100%" }}
              className="fixed inset-0 z-[110] bg-ink text-white p-6 sm:p-12 flex flex-col justify-center gap-8 sm:gap-12"
            >
              <button
                className="absolute top-6 right-6 sm:top-12 sm:right-12"
                onClick={() => setIsMenuOpen(false)}
              >
                <X className="w-8 h-8" />
              </button>
              <div className="space-y-6 sm:space-y-8">
                {["Authority", "Strategy", "Launchpads", "Process"].map(
                  (item) => (
                    <a
                      key={item}
                      href={`#${item.toLowerCase()}`}
                      className="block text-4xl sm:text-6xl font-display font-black uppercase tracking-tighter hover:text-primary transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item}
                    </a>
                  ),
                )}
              </div>
              <AgencyButton
                variant="primary"
                className="w-full py-3 px-4 text-xs sm:py-8 sm:px-6 sm:text-2xl"
                href={SIGNUP_URL}
                external
              >
                Create Free Account
              </AgencyButton>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hero Section: High-Impact Full-Width */}
        <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-paper pt-40 sm:pt-48 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-12">
          {/* Spotlight Effect */}
          <motion.div
            className="absolute inset-0 z-0 pointer-events-none opacity-40"
            animate={{
              background: `radial-gradient(600px circle at ${mousePos.x}px ${mousePos.y}px, rgba(53, 186, 207, 0.15), transparent 80%)`,
            }}
          />

          <motion.div
            style={{ y: heroY, scale: heroScale, opacity: heroOpacity }}
            className="max-w-6xl w-full flex flex-col items-center text-center relative z-10"
          >
            <h1 className="headline-hero font-display font-black tracking-[-0.07em] uppercase mb-10 sm:mb-16 text-center min-w-0 text-ink">
              Stop Being <span className="text-invisible-hero">Invisible.</span>{" "}
              Start <span className="text-primary">Dominating.</span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="text-base sm:text-xl lg:text-4xl text-ink/40 max-w-4xl mb-8 sm:mb-12 leading-[1.1] font-medium text-balance"
            >
              In the fast-moving world of Web3, if your project isn't appearing
              in AI-generated insights, you don't just have a marketing
              problem—you have an{" "}
              <span className="text-ink underline decoration-primary underline-offset-8">
                existence problem.
              </span>
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="text-base sm:text-lg lg:text-xl text-ink/60 max-w-3xl mb-10 sm:mb-16 leading-relaxed"
            >
              At Blockchain Wire, powered by Content Syndicate, we bridge the
              gap between "undiscovered gem" and "industry authority." Whether
              you’re a marketing agency scaling crypto clients or a founder
              ready for the big leagues, we provide the high-authority fuel your
              brand needs to ignite.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12"
            >
              <AgencyButton
                size="lg"
                className="w-full sm:w-auto py-3 px-4 text-xs sm:py-8 sm:px-16 sm:text-xl"
                href={SIGNUP_URL}
                external
              >
                Claim $100 Discount{" "}
                <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6" />
              </AgencyButton>
              <div className="flex items-center gap-4 sm:gap-8">
                <div className="w-px h-12 sm:h-16 bg-ink/10" />
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-ink/30 mb-1">
                    Referral Code
                  </span>
                  <span className="text-2xl sm:text-4xl font-mono font-bold text-ink">
                    SAVE100
                  </span>
                </div>
              </div>
            </motion.div>
            <p className="mt-12 text-[10px] font-bold uppercase tracking-widest text-ink/30">
              Blockchain Wire has packages to meet any budget.
            </p>
          </motion.div>

          {/* Background Decorative Elements */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <motion.div
              animate={{
                rotate: 360,
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -top-1/4 -right-1/4 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px]"
            />
            <motion.div
              animate={{
                rotate: -360,
                scale: [1, 1.2, 1],
              }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              className="absolute -bottom-1/4 -left-1/4 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px]"
            />
          </div>
        </section>

        {/* Marquee: Partner logos */}
        <div className="bg-paper relative z-20">
          <Marquee>
            {[
              { src: "/partners/benzinga.svg", alt: "Benzinga" },
              { src: "/partners/marketwatch.svg", alt: "MarketWatch" },
              { src: "/partners/bi-logo.svg", alt: "Business Insider" },
              { src: "/partners/ap news.svg", alt: "AP News" },
              { src: "/partners/Google News.svg", alt: "Google News" },
              { src: "/partners/bing.svg", alt: "Bing" },
              { src: "/partners/Bitcoinist.svg", alt: "Bitcoinist" },
              { src: "/partners/Newsbtc.svg", alt: "NewsBTC" },
              { src: "/partners/cryptopolitan.svg", alt: "Cryptopolitan" },
              { src: "/partners/Menafn.svg", alt: "MENAFN" },
              { src: "/partners/digital journal.svg", alt: "Digital Journal" },
            ].map(({ src, alt }) => (
              <div
                key={alt}
                className="flex items-center justify-center shrink-0 group cursor-default h-12 lg:h-14 min-h-[3rem]"
              >
                <img
                  src={src.replace(/\s/g, "%20")}
                  alt={alt}
                  className="h-full w-auto max-w-[140px] lg:max-w-[160px] object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                />
              </div>
            ))}
          </Marquee>
        </div>

        {/* Authority Section: Bento V2 */}
        <section
          id="authority"
          className="py-12 sm:py-24 lg:py-40 bg-paper relative z-20"
        >
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12">
            <div className="mb-10 sm:mb-16 lg:mb-24 flex flex-col lg:flex-row lg:items-end justify-between gap-6 sm:gap-12">
              <div className="max-w-3xl min-w-0">
                <span className="text-primary font-bold tracking-[0.5em] uppercase text-[10px] mb-4 sm:mb-8 block">
                  The Currency of Trust
                </span>
                <h2 className="text-4xl sm:text-6xl lg:text-[8vw] font-display font-black leading-[0.85] uppercase tracking-tighter">
                  Why Authority <br />
                  <span className="text-primary italic text-glow">
                    Matters.
                  </span>
                </h2>
              </div>
              <p className="text-base sm:text-xl text-ink/40 max-w-md leading-tight font-medium italic font-serif">
                "The difference between projects that invest in PR and those
                that rely solely on 'shilling' is staggering. Earned media is
                the only currency that still buys institutional trust."
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-8">
              {/* Main Bento Card */}
              <motion.div
                whileHover={{ y: -10 }}
                className="lg:col-span-12 bg-ink rounded-2xl sm:rounded-3xl lg:rounded-[4rem] p-6 sm:p-10 lg:p-20 text-white relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[150px] -mr-64 -mt-64 group-hover:bg-primary/10 transition-colors duration-700" />
                <div className="relative z-10">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 sm:gap-12 mb-10 sm:mb-20">
                    <div>
                      <h3 className="text-2xl sm:text-4xl lg:text-7xl font-display font-black uppercase leading-[0.9] mb-4 sm:mb-6">
                        The Hard <span className="text-primary">Numbers.</span>
                      </h3>
                      <p className="text-white/40 max-w-xl text-lg font-medium">
                        Earned media outperforms paid advertising and social
                        shilling across every critical metric.
                      </p>
                    </div>
                    <AgencyButton variant="white" className="text-ink">
                      Compare Performance
                    </AgencyButton>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                    {[
                      {
                        label: "Trust & Credibility",
                        with: "84% boost in brand trust",
                        without: "Seen as 'just another ad'",
                        icon: ShieldCheck,
                      },
                      {
                        label: "Search Visibility",
                        with: "85.5% of AI citations",
                        without: "Struggling against algorithms",
                        icon: Search,
                      },
                      {
                        label: "Conversion Rates",
                        with: "Up to 47% higher rates",
                        without: "High bounce; no proof",
                        icon: BarChart3,
                      },
                      {
                        label: "SEO & Impact",
                        with: "Tier-1 Backlinks",
                        without: "Ignored guest posts",
                        icon: TrendingUp,
                      },
                    ].map((stat, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className="p-5 sm:p-6 lg:p-10 bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl lg:rounded-[2.5rem] hover:bg-white/10 transition-all relative overflow-hidden group/card"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity" />
                        <stat.icon className="w-8 h-8 sm:w-10 sm:h-10 text-primary mb-4 sm:mb-8 relative z-10" />
                        <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4 sm:mb-6 relative z-10">
                          {stat.label}
                        </h4>
                        <div className="space-y-4 sm:space-y-6 relative z-10">
                          <div>
                            <div className="text-[8px] font-bold uppercase tracking-widest text-primary mb-1">
                              With PR
                            </div>
                            <div className="text-sm sm:text-lg font-bold text-white">
                              {stat.with}
                            </div>
                          </div>
                          <div className="pt-3 sm:pt-4 border-t border-white/5">
                            <div className="text-[8px] font-bold uppercase tracking-widest text-white/20 mb-1">
                              Without PR
                            </div>
                            <div className="text-xs sm:text-sm font-medium text-white/30">
                              {stat.without}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* AI Bento Card: High-End Tech Visualization */}
              <motion.div
                whileHover={{ y: -10 }}
                className="lg:col-span-12 bg-ink rounded-2xl sm:rounded-3xl lg:rounded-[4rem] p-6 sm:p-10 lg:p-24 flex flex-col lg:flex-row items-center gap-10 sm:gap-16 lg:gap-20 text-white relative overflow-hidden group border border-white/5"
              >
                {/* Animated Background Glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none group-hover:bg-primary/10 transition-colors duration-1000" />

                <div className="lg:w-1/2 relative z-10">
                  <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6 sm:mb-12">
                    <Cpu className="w-4 h-4 text-primary" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">
                      LLM Programming Protocol
                    </span>
                  </div>

                  <h3 className="text-2xl sm:text-4xl lg:text-8xl font-display font-black uppercase leading-[0.9] tracking-tighter mb-6 sm:mb-12">
                    Be Searchable <br />
                    <span className="text-primary italic">in the AI Era.</span>
                  </h3>

                  <div className="space-y-8">
                    <p className="text-xl lg:text-2xl font-medium leading-tight text-white/40 text-balance">
                      Search has changed. LLMs like{" "}
                      <span className="text-white">
                        Claude, Gemini, and ChatGPT
                      </span>{" "}
                      learn from authoritative news.
                    </p>
                    <p className="text-lg font-serif italic text-primary/80 leading-relaxed max-w-xl">
                      "By securing placements in global publications, you are
                      effectively programming the AI to recognize you as the
                      definitive expert in your niche."
                    </p>
                  </div>
                </div>

                <div className="lg:w-1/2 w-full relative min-w-0">
                  {/* Terminal UI Component */}
                  <div className="bg-[#0D0D0D] border border-white/10 rounded-xl sm:rounded-2xl lg:rounded-[2.5rem] overflow-hidden shadow-2xl relative group/terminal">
                    {/* Terminal Header */}
                    <div className="h-8 sm:h-12 bg-white/5 border-b border-white/10 flex items-center justify-between px-4 sm:px-8">
                      <div className="flex gap-1.5 sm:gap-2">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-white/10" />
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-white/10" />
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-white/10" />
                      </div>
                      <span className="text-[7px] sm:text-[8px] font-mono text-white/20 uppercase tracking-widest truncate max-w-[60%] sm:max-w-none">
                        ai_authority_engine_v2.0.4
                      </span>
                    </div>

                    {/* Terminal Content */}
                    <div className="p-4 sm:p-6 lg:p-10 font-mono text-[9px] sm:text-[11px] lg:text-xs space-y-3 sm:space-y-6 overflow-x-auto">
                      <div className="flex gap-2 sm:gap-4 min-w-0">
                        <span className="text-primary/40 flex-shrink-0">
                          01
                        </span>
                        <div className="flex gap-2 sm:gap-3 min-w-0 overflow-hidden">
                          <span className="text-primary flex-shrink-0">
                            [{">"}]
                          </span>
                          <span className="text-white/80 truncate">
                            initializing_deep_scan...
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 sm:gap-4 min-w-0">
                        <span className="text-primary/40 flex-shrink-0">
                          02
                        </span>
                        <div className="flex gap-2 sm:gap-3 min-w-0 overflow-hidden">
                          <span className="text-primary flex-shrink-0">
                            [{">"}]
                          </span>
                          <span className="text-white/80 break-all">
                            crawling:{" "}
                            <span className="text-primary">
                              yahoo_finance, business_insider
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 sm:gap-4 min-w-0">
                        <span className="text-primary/40 flex-shrink-0">
                          03
                        </span>
                        <div className="flex gap-2 sm:gap-3 min-w-0 overflow-hidden">
                          <span className="text-primary flex-shrink-0">
                            [{">"}]
                          </span>
                          <span className="text-white/80 truncate">
                            extracting_authority_signals...
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 sm:gap-4 min-w-0">
                        <span className="text-primary/40 flex-shrink-0">
                          04
                        </span>
                        <div className="flex gap-2 sm:gap-3 min-w-0 overflow-hidden">
                          <span className="text-primary flex-shrink-0">
                            [{">"}]
                          </span>
                          <span className="text-emerald-400 font-bold break-all">
                            MATCH_FOUND: [HIGH_AUTHORITY_VERIFIED]
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 sm:gap-4 pt-3 sm:pt-6 border-t border-white/5 min-w-0">
                        <span className="text-primary/40 flex-shrink-0">
                          05
                        </span>
                        <div className="flex gap-2 sm:gap-3 min-w-0 overflow-hidden">
                          <span className="text-primary flex-shrink-0">
                            [{">"}]
                          </span>
                          <span className="text-primary font-bold animate-pulse break-all">
                            UPDATING_LLM_KNOWLEDGE_BASE...
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 h-12 sm:h-24 bg-gradient-to-t from-ink to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-4 right-4 sm:bottom-6 sm:left-10 sm:right-10 flex justify-end items-center">
                      <Activity className="w-3 h-3 sm:w-4 sm:h-4 text-white/20" />
                    </div>
                  </div>

                  {/* Decorative Floating Element */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="absolute -top-4 -right-4 w-16 h-16 sm:-top-8 sm:-right-8 sm:w-32 sm:h-32 bg-primary/20 rounded-full blur-3xl"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Strategy Section: Dark Immersive */}
        <section
          id="strategy"
          className="py-12 sm:py-24 lg:py-48 bg-ink text-white overflow-hidden relative z-20"
        >
          {/* Premium Glow Accents */}
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />

          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row gap-12 sm:gap-20 lg:gap-32 items-center">
              <div className="lg:w-1/2 min-w-0">
                <div className="inline-flex items-center gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-white/5 border border-white/10 mb-6 sm:mb-12">
                  <ZapIcon className="w-4 h-4 text-primary" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em]">
                    We Don't Just Distribute. We Strategize.
                  </span>
                </div>
                <h2 className="text-4xl sm:text-7xl lg:text-[6vw] font-display font-black leading-[0.85] uppercase mb-8 sm:mb-16 tracking-tighter">
                  We Guide. <br />
                  You <span className="text-primary italic">Dominate.</span>
                </h2>
                <p className="text-base sm:text-xl lg:text-2xl text-white/30 mb-8 sm:mb-16 leading-tight max-w-xl font-medium">
                  Traditional PR agencies charge $5,000–$15,000 per month. If
                  you aren't ready for that kind of overhead, we offer a
                  hands-on, pay-as-you-go approach that puts the power back in
                  your hands.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-12">
                  <div className="group cursor-default">
                    <div className="flex items-center gap-4 sm:gap-6 mb-4 sm:mb-6">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full border-2 border-primary flex items-center justify-center text-primary font-display font-black text-sm sm:text-xl group-hover:bg-primary group-hover:text-ink transition-all duration-500">
                        01
                      </div>
                      <h4 className="text-xl sm:text-2xl font-display font-black uppercase tracking-tight">
                        The Strategy
                      </h4>
                    </div>
                    <p className="text-white/30 pl-0 sm:pl-20 text-sm sm:text-base font-medium leading-relaxed">
                      Don't have a "hook"? We’ll help you find one. Our experts
                      work with you to craft a narrative that journalists want
                      to cover and AI models want to cite.
                    </p>
                  </div>
                  <div className="group cursor-default">
                    <div className="flex items-center gap-4 sm:gap-6 mb-4 sm:mb-6">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full border-2 border-primary flex items-center justify-center text-primary font-display font-black text-sm sm:text-xl group-hover:bg-primary group-hover:text-ink transition-all duration-500">
                        02
                      </div>
                      <h4 className="text-xl sm:text-2xl font-display font-black uppercase tracking-tight">
                        The Execution
                      </h4>
                    </div>
                    <p className="text-white/30 pl-0 sm:pl-20 text-sm sm:text-base font-medium leading-relaxed">
                      Choose between Done-For-You optimization or our
                      streamlined DIY platform for massive reach across our
                      high-authority network.
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2 relative">
                <div className="relative z-10 grid grid-cols-1 gap-4 sm:gap-8">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ x: 20 }}
                    className="bg-white/5 border border-white/10 p-6 sm:p-10 lg:p-16 rounded-2xl sm:rounded-3xl lg:rounded-[4rem] hover:bg-white/10 transition-all duration-500 group"
                  >
                    <Layers className="w-10 h-10 sm:w-16 sm:h-16 text-primary mb-6 sm:mb-12 group-hover:scale-110 transition-transform" />
                    <h4 className="text-2xl sm:text-4xl font-display font-black uppercase mb-4 sm:mb-6">
                      Done-For-You
                    </h4>
                    <p className="text-white/40 text-sm sm:text-lg font-medium leading-tight">
                      We’ll write, optimize, and distribute your story to Tier-1
                      outlets like Yahoo Finance and Business Insider.
                    </p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    whileHover={{ x: 20 }}
                    className="bg-primary p-6 sm:p-10 lg:p-16 rounded-2xl sm:rounded-3xl lg:rounded-[4rem] text-ink group"
                  >
                    <Zap className="w-10 h-10 sm:w-16 sm:h-16 mb-6 sm:mb-12 group-hover:scale-110 transition-transform" />
                    <h4 className="text-2xl sm:text-4xl font-display font-black uppercase mb-4 sm:mb-6">
                      Do-It-Yourself
                    </h4>
                    <p className="text-ink font-bold text-sm sm:text-lg leading-tight">
                      Already have the content? Use our streamlined platform to
                      push your release out to our massive network of
                      high-authority sites.
                    </p>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section: Refined Cards */}
        <section
          id="launchpads"
          className="py-12 sm:py-24 lg:py-48 bg-paper relative z-20"
        >
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12">
            <div className="text-center mb-10 sm:mb-20 lg:mb-32">
              <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-primary mb-4 sm:mb-8 block">
                Fuel Your Mission
              </span>
              <h2 className="text-4xl sm:text-7xl lg:text-[8vw] font-display font-black uppercase tracking-tighter leading-[0.85]">
                Choose Your <br />
                <span className="text-primary italic">Launchpad.</span>
              </h2>
              <p className="mt-6 sm:mt-12 text-sm sm:text-xl text-ink/40 font-bold uppercase tracking-widest">
                Blockchain Wire has packages to meet any budget.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {pricingPlans.map((plan, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.8,
                    delay: idx * 0.1,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  whileHover={{ y: -20, scale: 1.02 }}
                  className={`relative p-5 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl lg:rounded-3xl border transition-all duration-700 flex flex-col ${plan.highlight ? "bg-ink text-white border-ink shadow-[0_40px_80px_-20px_rgba(0,0,0,0.3)]" : "bg-white border-ink/5 hover:border-primary/40"}`}
                >
                  {plan.highlight && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5, type: "spring" }}
                      className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 bg-primary text-ink text-[8px] sm:text-[9px] font-black uppercase px-3 sm:px-4 py-1 sm:py-1.5 rounded-full tracking-widest whitespace-nowrap"
                    >
                      Recommended
                    </motion.div>
                  )}
                  <div className="mb-8 sm:mb-16">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-[0.3em] mb-2 sm:mb-4 block ${plan.highlight ? "text-primary" : "text-ink/30"}`}
                    >
                      {plan.price}
                    </span>
                    <h3 className="text-3xl sm:text-4xl lg:text-5xl font-display font-black uppercase tracking-tighter">
                      {plan.name}
                    </h3>
                  </div>

                  <ul className="space-y-4 sm:space-y-6 lg:space-y-8 mb-8 sm:mb-16 flex-grow">
                    {plan.includes.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-4 text-sm font-bold uppercase tracking-tight"
                      >
                        <div
                          className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${plan.highlight ? "bg-primary" : "bg-ink"}`}
                        />
                        <span
                          className={
                            plan.highlight ? "text-white/40" : "text-ink/40"
                          }
                        >
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <AgencyButton
                    variant={plan.highlight ? "primary" : "outline"}
                    className="w-full py-2.5 px-4 text-xs sm:py-3 sm:px-5 sm:text-sm uppercase tracking-widest"
                    href={SIGNUP_URL}
                    external
                  >
                    Launch {plan.name}
                  </AgencyButton>
                </motion.div>
              ))}
            </div>

            <motion.div
              whileHover={{ scale: 1.01 }}
              className="mt-12 sm:mt-20 lg:mt-32 bg-slate-100 rounded-2xl sm:rounded-3xl lg:rounded-[5rem] p-6 sm:p-10 lg:p-24 flex flex-col lg:flex-row justify-between items-center gap-8 sm:gap-12 lg:gap-16 relative overflow-hidden group border border-ink/5"
            >
              <div className="max-w-2xl relative z-10 min-w-0">
                <span className="text-primary font-bold tracking-[0.3em] uppercase text-[10px] mb-4 sm:mb-8 block">
                  Broadcast Network Expansion
                </span>
                <h4 className="text-2xl sm:text-4xl lg:text-7xl font-display font-black uppercase text-ink leading-[0.9] mb-4 sm:mb-8">
                  Want more? <br />
                  <span className="text-primary">Let's talk!</span>
                </h4>
                <p className="text-ink/40 text-sm sm:text-lg lg:text-xl font-medium leading-tight">
                  Get discovered on premier Broadcast TV and Audio Networks by
                  reaching 1 million viewers and listeners with a 60 second
                  power interview. CMP starting at $5.
                </p>
              </div>
              <div className="relative z-10">
                <AgencyButton
                  variant="primary"
                  size="lg"
                  href={CONTACT_URL}
                  className="py-1.5 px-2.5 text-[10px] sm:py-3 sm:px-6 sm:text-sm"
                >
                  Talk to an Expert
                </AgencyButton>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Process Section: The 3 Steps */}
        <section
          id="process"
          className="py-12 sm:py-24 lg:py-48 bg-ink text-white relative z-20"
        >
          {/* Premium Glow Accents */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/[0.02] rounded-full blur-[200px] pointer-events-none" />

          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 sm:gap-10 lg:gap-12 mb-12 sm:mb-20 lg:mb-32">
              <div className="max-w-3xl min-w-0">
                <span className="text-primary font-bold tracking-[0.5em] uppercase text-[10px] mb-4 sm:mb-8 block">
                  Onboarding Protocol
                </span>
                <h2 className="text-4xl sm:text-7xl lg:text-9xl font-display font-black leading-[0.9] sm:leading-[0.85] uppercase tracking-tighter">
                  Claim Your Spot
                  <br className="hidden sm:block" />
                  on <span className="text-primary italic">Page One.</span>
                </h2>
              </div>
              <p className="text-white/30 text-base sm:text-xl lg:text-2xl font-medium max-w-md leading-snug lg:leading-tight">
                Stop wasting thousands on "hope-and-pray" marketing. Get the
                authority your business deserves today.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {[
                {
                  step: "01",
                  title: "Create Free Account",
                  desc: "It takes 5 minutes. Enter SAVE100 in the Referral Field. Upload your logo and company info.",
                  icon: Command,
                },
                {
                  step: "02",
                  title: "Get Verified",
                  desc: "We’ll verify your account and email to ensure network integrity and high-authority standards.",
                  icon: ShieldCheck,
                },
                {
                  step: "03",
                  title: "Upload & Save",
                  desc: "Once verified, log back in and select your package. At checkout, select your promo code to save $100 instantly.",
                  icon: Zap,
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  whileHover={{ y: -20 }}
                  className="p-6 sm:p-10 lg:p-16 bg-white/5 border border-white/10 rounded-2xl sm:rounded-3xl lg:rounded-[4rem] group hover:bg-white/10 transition-all duration-500"
                >
                  <div className="flex justify-between items-start mb-6 sm:mb-12">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary flex items-center justify-center">
                      <item.icon className="text-ink w-6 h-6 sm:w-8 sm:h-8" />
                    </div>
                    <span className="text-4xl sm:text-6xl font-display font-black text-white/5 group-hover:text-primary/20 transition-colors uppercase">
                      {item.step}
                    </span>
                  </div>
                  <h4 className="text-xl sm:text-2xl lg:text-3xl font-display font-black uppercase mb-4 sm:mb-8">
                    {item.title}
                  </h4>
                  <p className="text-white/30 text-sm sm:text-base lg:text-lg font-medium leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA: Massive Scale */}
        <section className="py-16 sm:py-32 lg:py-64 bg-paper text-ink text-center relative overflow-hidden z-20">
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 min-w-0">
            <motion.h2
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
              className="text-4xl sm:text-7xl lg:text-[8vw] font-display font-black leading-[0.85] uppercase mb-10 sm:mb-20 tracking-tighter"
            >
              Ready to <br />
              <span className="text-primary italic">Dominate?</span>
            </motion.h2>
            <p className="text-base sm:text-2xl text-ink/30 mb-10 sm:mb-20 max-w-2xl mx-auto font-medium">
              Join 2,500+ agencies and owners who are finally getting noticed
              and dominating the chain.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12">
              <AgencyButton
                size="lg"
                className="py-3 px-4 text-xs sm:py-8 sm:px-20 sm:text-3xl w-full sm:w-auto max-w-sm sm:max-w-none"
                href={SIGNUP_URL}
                external
              >
                Claim Your $100 Discount
              </AgencyButton>
              <div className="text-left min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-ink/30 mb-2">
                  Referral Code
                </div>
                <div className="text-3xl sm:text-5xl font-mono font-bold text-primary tracking-tighter">
                  SAVE100
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer: Minimalist Agency Style */}
        <footer className="bg-paper py-12 sm:py-20 lg:py-32 border-t border-ink/5 relative z-20">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row justify-between items-start gap-12 sm:gap-16 lg:gap-24">
              <div className="max-w-md min-w-0">
                <div className="flex items-center gap-3 mb-6 sm:mb-12">
                  <img
                    src="/Logo Horizontal Color.svg"
                    alt="Blockchain Wire"
                    className="h-8 sm:h-10 w-auto object-contain"
                  />
                </div>
                <p className="text-ink/30 text-sm sm:text-lg leading-tight font-medium mb-6 sm:mb-12">
                  The high-authority fuel for Web3 brands. Powered by Content
                  Syndicate. Bridging the gap between undiscovered gems and
                  industry authorities.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="https://www.instagram.com/blockchainwire.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-ink/10 flex items-center justify-center hover:bg-ink hover:text-white transition-all group"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </a>
                  <a
                    href="https://www.facebook.com/share/1CRonNrbqd/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-ink/10 flex items-center justify-center hover:bg-ink hover:text-white transition-all group"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/blockchain-wire/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-ink/10 flex items-center justify-center hover:bg-ink hover:text-white transition-all group"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </a>
                  <a
                    href="https://t.me/Blockchain_Wire"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-ink/10 flex items-center justify-center hover:bg-ink hover:text-white transition-all group"
                    aria-label="Telegram"
                  >
                    <Send className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </a>
                  <a
                    href="https://twitter.com/blockchain_wire"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-ink/10 flex items-center justify-center hover:bg-ink hover:text-white transition-all group"
                    aria-label="Twitter"
                  >
                    <Twitter className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </a>
                  <a
                    href="https://blockchainwire.io/feed-rss.xml"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-ink/10 flex items-center justify-center hover:bg-ink hover:text-white transition-all group"
                    aria-label="RSS"
                  >
                    <Rss className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-12 sm:gap-16 lg:gap-24">
                <div className="space-y-10">
                  <h5 className="text-[10px] font-bold uppercase tracking-[0.5em] text-ink/20">
                    Protocol
                  </h5>
                  <ul className="space-y-6 text-sm font-bold uppercase tracking-tighter">
                    <li>
                      <a
                        href="#authority"
                        className="hover:text-primary transition-colors"
                      >
                        Authority
                      </a>
                    </li>
                    <li>
                      <a
                        href="#strategy"
                        className="hover:text-primary transition-colors"
                      >
                        Strategy
                      </a>
                    </li>
                    <li>
                      <a
                        href="#launchpads"
                        className="hover:text-primary transition-colors"
                      >
                        Launchpads
                      </a>
                    </li>
                    <li>
                      <a
                        href="#process"
                        className="hover:text-primary transition-colors"
                      >
                        Process
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="space-y-10">
                  <h5 className="text-[10px] font-bold uppercase tracking-[0.5em] text-ink/20">
                    Legal
                  </h5>
                  <ul className="space-y-6 text-sm font-bold uppercase tracking-tighter">
                    <li>
                      <button
                        onClick={() =>
                          setLegalModal({
                            isOpen: true,
                            title: "Privacy Policy",
                            content: PRIVACY_POLICY,
                          })
                        }
                        className="hover:text-primary transition-colors cursor-pointer"
                      >
                        Privacy
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() =>
                          setLegalModal({
                            isOpen: true,
                            title: "Terms & Conditions",
                            content: TERMS_AND_CONDITIONS,
                          })
                        }
                        className="hover:text-primary transition-colors cursor-pointer"
                      >
                        Terms
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-12 sm:mt-20 lg:mt-32 pt-8 sm:pt-12 lg:pt-16 border-t border-ink/5 flex flex-col sm:flex-row justify-between items-center gap-6 sm:gap-12">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-ink/20">
                © {new Date().getFullYear()} Blockchain Wire. All rights
                reserved.
              </p>
            </div>
          </div>
        </footer>
        <LegalModal
          isOpen={legalModal.isOpen}
          onClose={() => setLegalModal({ ...legalModal, isOpen: false })}
          title={legalModal.title}
          content={legalModal.content}
        />
      </motion.div>
    </div>
  );
}
